// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## linesAndSpaces.js
//
// Students draw notes on particular lines & spaces.
import { isClefName } from 'shared/definitions/musicTheory/clefs';
import Clef from '../uTheory.Notation/clef';
import Staff from '../uTheory.Notation/staff';
import NoteHolder from '../uTheory.Notation/noteHolder';
import Measure from '../uTheory.Notation/measure';
import Editable from '../uTheory.Notation/editable';
import Voice from '../uTheory.Notation/voice';
import KeySignature from '../uTheory.Notation/keySignature';
import Music from '../uTheory.Notation/music';

import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import utils from '../utils';
import type { NoteDrawOptions } from './noteDraw';
import NoteDraw from './noteDraw';
import type { InteractiveSettings } from './interactivePrototype';

const settings: InteractiveSettings = {
  instructions: {
    ariaLive: 'alert',
    ariaLiveRegion: 0,
  },
  editor: {
    fixedWidth: 150,
    ariaTitle: {
      keySignature: false,
      rhythm: false,
      barline: false,
      lineOrSpace: true,
      accidental: false,
      octave: false,
      selected: false,
      clef: false,
    },
    ariaLive: 'assertive',
  },
  notation: false,
  piano: false,
  letters: { letterButtons: false, checkButton: true },
  checkMark: {},
  player: {},
  progressTracker: true,
  oneTryOnly: false,
};

interface LinesAndSpacesOptions extends NoteDrawOptions {
  legers?: boolean;
}

// Note: This interactive extends NoteDraw -- which extends InteractivePrototype.
class LinesAndSpaces extends NoteDraw {
  declare options: LinesAndSpacesOptions;

  init(displayDiv: HTMLDivElement, options: LinesAndSpacesOptions) {
    this.targetSpeedFromSkills = LinesAndSpaces.registry[0]!.skills;
    utils.merge(this.settings, settings);
    return super.init(displayDiv, options);
  }

  // Overriding these methods / properties:

  generateQuestions() {
    this.options.clefs = ['treble'];
    this.questions = [];
    this.questionPrompt = this.options.questionPrompt;
    if (typeof this.questionPrompt === 'undefined') {
      const totalQ =
        this.options.drill !== true ? this.options.questions?.length : this.options.maxQuestions;
      this.questionPrompt = `Draw a note {question}.<br /><small>{question} of ${totalQ}</small>`;
    }

    const clef = new Clef(isClefName(this.options.clef) ? this.options.clef : undefined);
    this.clef = clef;
    const staff = new Staff(clef);
    this.noteHolder = new NoteHolder('', 'w');
    this.noteHolder.editable = new Editable(this.noteHolder, {
      pitch: true,
      accidental: false,
      deleteable: false,
      selectable: true,
    });
    const measure = new Measure(this.noteHolder);

    const voice = new Voice(measure);
    voice.setStaff(staff);

    const keySig = new KeySignature('C', 'Major');
    this.music = new Music(staff, voice, keySig);

    if (this.options.drill === true) {
      this.options.questions = this.generateRandomQuestions().map((q) => q.line);
    }
    this.questions = [];
    this.options.questions!.forEach((question) => {
      if (typeof question !== 'number')
        throw Error('Only number questions are allowed for LinesAndSpaces');
      const note = clef.getNoteAtLine(question);
      const text = note.toLineOrSpaceString(clef);

      this.questions.push({
        text: [text, this.questions.length + 1],
        note,
        clef,
        skills: ['lines_spaces'],
      });
    }, this);
  }

  generateRandomQuestions() {
    const generator = new QuestionGenerator();
    if (typeof this.options.maxQuestions === 'undefined') {
      this.options.maxQuestions = 15;
    }

    this.options.legers = !!this.options.legers;

    const questions = generator.lines_spaces(this.options.maxQuestions, this.options);
    this.debug(questions);
    return questions;
  }

  static registry: {
    skills: string[];
    options: LinesAndSpacesOptions;
    description: string;
  }[] = [
    {
      description: 'Draw notes on lines and spaces of the staff',
      skills: ['lines_spaces'],
      options: {
        legers: false,
        clefs: ['treble'],
        skills: ['lines_spaces'],
      },
    },
  ];
}

export default LinesAndSpaces;
